// src/pages/ViewOrderTracking.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  ArrowLeft,
  AlertCircle,
  Truck,
  ClipboardList,
  Clock,
  Plus,
  Trash2,
  X,
  MapPin,
} from "lucide-react";
import {
  GetSingleTrackingThunk,
  AddTrackingHistoryThunk,
  DeleteTrackingHistoryThunk,
} from "../../redux/states/tracking/thunk";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";

const ViewOrderTracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tracking_number } = useParams(); // assuming route param is uuid
  const { singleTracking, loading } = useSelector((state) => state.tracking);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [formData, setFormData] = useState({
    tracking_number: "",
    status: "pending",
    location: "",
    remarks: "",
  });

  const statusOptions = [
    "pending",
    "processing",
    "confirmed",
    "dispatched",
    "in_transit",
    "delivered",
    "cancelled",
    "failed",
    "refunded",
  ];

  useEffect(() => {
    if (tracking_number) {
      dispatch(GetSingleTrackingThunk(tracking_number));
    }
  }, [dispatch, tracking_number]);

  useEffect(() => {
    if (singleTracking) {
      setFormData((prev) => ({
        ...prev,
        tracking_number: singleTracking?.tracking?.tracking_number,
      }));
    }
  }, [singleTracking]);

  const tracking = singleTracking?.tracking;
  const history = singleTracking?.history || [];

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading tracking details...
            </p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!tracking) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
          <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Tracking Not Found
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold"
          >
            Back
          </button>
        </div>
      </NonAuthLayout>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-700";
      case "in-transit":
        return "bg-yellow-100 text-yellow-700";
      case "pending":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleAddSubmit = () => {
    dispatch(AddTrackingHistoryThunk(formData)).then(() => {
      setAddModalOpen(false);
      setFormData({
        tracking_number: singleTracking?.tracking?.tracking_number,
        status: "pending",
        location: "",
        remarks: "",
      });
      dispatch(GetSingleTrackingThunk(tracking_number)); // refresh table
    });
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(DeleteTrackingHistoryThunk(deleteId)).then(() => {
      setDeleteModalOpen(false);
      setDeleteId(null);
      dispatch(GetSingleTrackingThunk(tracking_number)); // refresh table
    });
  };

  return (
    <NonAuthLayout>
      <div className="min-h-scree py-2 px-2 sm:px-2 lg:px-2">
        <div className="max-w-12xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Tracking Details
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(
                tracking?.current_status
              )}`}
            >
              {tracking?.current_status}
            </span>
          </div>

          {/* Tracking Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Tracking Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow
                label="Tracking Number"
                value={tracking?.tracking_number}
              />
              <InfoRow label="Courier Name" value={tracking?.courier_name} />
              <InfoRow label="Order ID" value={tracking?.order_id} />
              <InfoRow
                label="Current Status"
                value={tracking?.current_status}
              />
              <InfoRow
                label="Current Location"
                value={tracking?.current_location}
              />
              <InfoRow label="Remarks" value={tracking?.remarks} />
              <InfoRow
                label="Created At"
                value={new Date(tracking?.created_at).toLocaleString("en-IN")}
              />
              <InfoRow
                label="Updated At"
                value={new Date(tracking?.updated_at).toLocaleString("en-IN")}
              />
            </div>
          </div>

          <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-center justify-between bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3 mb-4 sm:mb-0">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
                Tracking History
              </h2>
              <button
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                onClick={() => setAddModalOpen(true)}
              >
                <Plus className="w-5 h-5" />
                Add New Record
              </button>
            </div>

            {/* History Table */}
            {history.length > 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Remarks
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Date & Time
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {history?.map((item, index) => (
                        <tr
                          key={item.id}
                          className="hover:bg-gray-50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                item.status === "Delivered"
                                  ? "bg-green-100 text-green-800"
                                  : item.status === "In Transit"
                                  ? "bg-blue-100 text-blue-800"
                                  : item.status === "Out for Delivery"
                                  ? "bg-orange-100 text-orange-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {item.location}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                            {item.remarks || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(item.created_at).toLocaleString("en-IN")}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="flex items-center gap-2 text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Clock className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No tracking history
                </h3>
                <p className="text-gray-500 mb-4">
                  Start by adding your first tracking record
                </p>
                <button
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 font-medium"
                  onClick={() => setAddModalOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                  Add First Record
                </button>
              </div>
            )}

            {/* Add Modal */}
            {addModalOpen && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50 p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md transform transition-all">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900">
                      Add Tracking Record
                    </h3>
                    <button
                      onClick={() => setAddModalOpen(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 space-y-4">
                    {/* Tracking Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tracking Number
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 px-4 py-3 rounded-lg bg-gray-50 cursor-not-allowed"
                        value={singleTracking?.tracking?.tracking_number || ""}
                        disabled
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                        value={formData.status}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.value })
                        }
                      >
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                        placeholder="Enter location"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData({ ...formData, location: e.target.value })
                        }
                      />
                    </div>

                    {/* Remarks */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remarks
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
                        placeholder="Enter remarks (optional)"
                        value={formData.remarks}
                        onChange={(e) =>
                          setFormData({ ...formData, remarks: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                    <button
                      className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium rounded-lg transition-colors duration-200"
                      onClick={() => setAddModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
                      onClick={handleAddSubmit}
                    >
                      <Plus className="w-4 h-4" />
                      Add Record
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Modal */}
            <ConfirmDeleteModal
              open={deleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
              onConfirm={handleConfirmDelete}
              title="Delete Tracking Record"
              message="Are you sure you want to delete this tracking record? This action cannot be undone and will permanently remove all tracking data."
            />
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

// InfoRow Component
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
    <span className="font-medium text-gray-700">{label}</span>
    <span className="text-gray-900">{value || "—"}</span>
  </div>
);

export default ViewOrderTracking;
